import { useContext, useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { DomainElement } from '../../../library/Core/SmartTypes';
import { InstituteSearchCriteria } from './BurnerAnalysisService';

interface Props {
    criteria: InstituteSearchCriteria;
    onChange: (next: InstituteSearchCriteria) => void;
}

const SearchCriteria = ({ criteria, onChange }: Props) => {
    const { globalState } = useContext(GlobalContext);
    const domain = globalState?.domain;

    const schoolGroupOptions = useMemo<DomainElement[]>(
        () => (domain?.get('SCHOOL_GROUP') as DomainElement[]) ?? [],
        [domain]
    );
    const instituteOptions = useMemo<DomainElement[]>(
        () => (domain?.get('INSTITUTE_DOMAIN') as DomainElement[]) ?? [],
        [domain]
    );
    const clientStatusOptions = useMemo<DomainElement[]>(
        () => (domain?.get('SCHOOL_CLIENT_STATUS_CODE') as DomainElement[]) ?? [],
        [domain]
    );
    const instituteTypeOptions = useMemo<DomainElement[]>(
        () => (domain?.get('INSTITUTE_TYPE_CODE') as DomainElement[]) ?? [],
        [domain]
    );

    const instituteSelectOptions = useMemo(
        () => instituteOptions.map((d) => ({ value: String(d.code), label: d.value })),
        [instituteOptions]
    );

    const loadInstituteOptions = (input: string, callback: (opts: any[]) => void) => {
        if (input.length < 3) {
            callback([]);
            return;
        }
        const upper = input.toUpperCase();
        callback(instituteSelectOptions.filter((o) => o.label?.toUpperCase().includes(upper)).slice(0, 50));
    };

    const selectedInstitute = useMemo(() => {
        if (!criteria.instituteId) return null;
        return instituteSelectOptions.find((o) => String(o.value) === String(criteria.instituteId)) ?? null;
    }, [criteria.instituteId, instituteSelectOptions]);

    const update = (key: keyof InstituteSearchCriteria, value: string) => {
        onChange({ ...criteria, [key]: value });
    };

    return (
        <div className="fitergroup mb-3">
            <div className="accordion-collapse collapse show">
                <div className="accordion-body p-4">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label font-13 font-500 text-uppercase text-muted">Institute Group</label>
                            <select
                                className="form-select"
                                value={criteria.instituteGroupId ?? ''}
                                onChange={(e) => update('instituteGroupId', e.target.value)}>
                                <option value="">All groups</option>
                                {schoolGroupOptions.map((g) => (
                                    <option key={String(g.code)} value={String(g.code)}>
                                        {g.value}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label font-13 font-500 text-uppercase text-muted">Institute Name</label>
                            <div className="input-group">
                                <div className="input-group-text bg-white rounded-start">
                                    <i className="bi bi-search fs-6"></i>
                                </div>
                                <div className="flex-fill form-control border-start-0 p-0">
                                    <AsyncSelect
                                        cacheOptions
                                        loadOptions={loadInstituteOptions}
                                        value={selectedInstitute}
                                        onChange={(opt: any) => update('instituteId', opt ? opt.value : '')}
                                        placeholder="Type 3+ chars..."
                                        isClearable
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label font-13 font-500 text-uppercase text-muted">Client Type</label>
                            <select
                                className="form-select"
                                value={criteria.clientType ?? ''}
                                onChange={(e) => update('clientType', e.target.value)}>
                                <option value="">All</option>
                                {clientStatusOptions.map((opt) => (
                                    <option key={String(opt.code)} value={String(opt.code)}>
                                        {opt.value}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label font-13 font-500 text-uppercase text-muted">Institute Type</label>
                            <select
                                className="form-select"
                                value={criteria.instituteTypeCode ?? ''}
                                onChange={(e) => update('instituteTypeCode', e.target.value)}>
                                <option value="">All</option>
                                {instituteTypeOptions.map((opt) => (
                                    <option key={String(opt.code)} value={String(opt.code)}>
                                        {opt.value}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchCriteria;
